import { useResizeObserver } from '@react-aria/utils';
import * as stylex from '@stylexjs/stylex';
import { formatForDisplay } from '@tanstack/hotkeys';
import { useHotkey } from '@tanstack/react-hotkeys';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useContext, useEffect, useRef, useState } from 'react';
import type React from 'react';
import { useFocusWithin, useHover } from 'react-aria';
import {
  Button as ButtonCore,
  DEFAULT_SLOT,
  DialogTrigger,
  OverlayTriggerStateContext,
  Provider,
  TooltipTrigger,
  TooltipTriggerStateContext,
} from 'react-aria-components';

import { LOCAL_STORAGE } from '@/constants/storage';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback.hook';
import {
  ButtonContext,
  CloseStrokeIcon,
  Dialog,
  DIALOG_SLOTS,
  DialogBar,
  FilterV2StrokeIcon,
  FORM_SLOTS,
  Heading,
  IconButton,
  Kbd,
  GridList,
  GridListItem,
  Popover,
  SEARCH_FIELD_SLOTS,
  SearchBar,
  SearchField,
  SearchRightStrokeIcon,
  Tooltip,
  useLocalStorage,
  useMediaQuery,
  ViewContext,
  type DialogProps,
  type SearchBarProps,
  type SearchFieldProps,
  Text,
  View,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { $ } from '@/libs/serenity-ui/tokens.stylex';
import { Route } from '@/routes/_main/_list';
import type { Resource } from '@/types/resource.type';

const MAX_HISTORY_SIZE = 20;

interface UseSearchHistoryResult {
  history: string[];
  addToHistory: (value: string) => void;
  removeFromHistory: (index: number) => void;
}

function useSearchHistory (resource: Resource): UseSearchHistoryResult {
  const storageConfig = LOCAL_STORAGE.searchHistory(resource);
  const [ history, setHistory ] = useLocalStorage<string[]>(storageConfig.key, storageConfig.initialValue);

  const addToHistory = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return;
    }
    const current: string[] = JSON.parse(localStorage.getItem(storageConfig.key) ?? '[]') as string[];
    const deduped = [ trimmed, ...current.filter((item) => item !== trimmed) ];
    setHistory(deduped.slice(0, MAX_HISTORY_SIZE));
  };

  const removeFromHistory = (index: number) => {
    setHistory((prev) => prev.filter((_, i) => i !== index));
  };

  return { history, addToHistory, removeFromHistory };
}

interface SearchProps {
  searchFilterId: string;
  resource: Resource;
  children: DialogProps['children'];
}

export function Search (props: SearchProps) {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const location = useLocation();

  const isSmallScreen = useMediaQuery(breakpoints.small);

  const searchValueFromUrl = props.searchFilterId
    ? (search.columnFilters.find((f) => f.id === props.searchFilterId)?.value as string) ?? ''
    : '';

  const [ searchInput, setSearchInput ] = useState(searchValueFromUrl);
  const [ prevUrlValue, setPrevUrlValue ] = useState(searchValueFromUrl);
  if (prevUrlValue !== searchValueFromUrl) {
    setPrevUrlValue(searchValueFromUrl);
    setSearchInput(searchValueFromUrl);
  }

  const { history, addToHistory, removeFromHistory } = useSearchHistory(props.resource);

  const debouncedNavigate = useDebouncedCallback((value: string) => {
    if (!props.searchFilterId) {
      return;
    }
    navigate({
      to: location.pathname,
      search: {
        ...search,
        columnFilters: [
          ...search.columnFilters.filter((f) => f.id !== props.searchFilterId),
          ...(value ? [{ id: props.searchFilterId, value }] : []),
        ],
        pagination: { ...search.pagination, pageIndex: 0 },
      },
    });
  }, 300);

  const handleChange = (value: string) => {
    setSearchInput(value);
    debouncedNavigate(value);
  };

  const handleClear = () => {
    if (!props.searchFilterId) {
      return;
    }
    setSearchInput('');
    navigate({
      to: location.pathname,
      search: {
        ...search,
        columnFilters: search.columnFilters.filter((f) => f.id !== props.searchFilterId),
        pagination: { ...search.pagination, pageIndex: 0 },
      },
    });
  };

  const isFiltered = search.columnFilters.some((f) => f.id !== props.searchFilterId);

  return isSmallScreen ?
    <MobileSearch
      history={history}
      isFiltered={isFiltered}
      value={searchInput}
      onBlurSave={addToHistory}
      onChange={handleChange}
      onClear={handleClear}
      onRemoveHistory={removeFromHistory}
      onSelectHistory={handleChange}>
      {props.children}
    </MobileSearch> :
    <DesktopSearch
      history={history}
      value={searchInput}
      onBlurSave={addToHistory}
      onChange={handleChange}
      onClear={handleClear}
      onRemoveHistory={removeFromHistory}
      onSelectHistory={handleChange}>
      {props.children}
    </DesktopSearch>;
}

interface HistoryProps {
  history: string[];
  onSelectHistory: (value: string) => void;
  onRemoveHistory: (index: number) => void;
  onBlurSave: (value: string) => void;
}

interface MobileSearchProps extends Pick<SearchBarProps, 'value' | 'onChange' | 'onClear'>, Pick<DialogProps, 'children'>, HistoryProps {
  /** Affects the style of the filter icon */
  isFiltered: boolean;
}

function MobileSearch (props: MobileSearchProps) {
  const [ isSearchActive, setIsSearchActive ] = useState(false);

  useEffect(() => {
    if (isSearchActive) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [ isSearchActive ]);

  const items = props.history
    .filter((item) => item.trim() !== props.value?.trim())
    .map((item, index) => ({ id: item, index, label: item }));

  return (
    <>
      <TooltipTrigger>
        <IconButton aria-label="Search" onPress={() => setIsSearchActive(true)}>
          <SearchRightStrokeIcon color={props.value ? 'interactive' : undefined} strokeWidth={props.value ? 1.5 : undefined} />
        </IconButton>
        <Tooltip>Search</Tooltip>
      </TooltipTrigger>

      {props.children &&
        <TooltipTrigger>
          <DialogTrigger>
            <IconButton aria-label="Filter">
              <FilterV2StrokeIcon color={props.isFiltered ? 'interactive' : undefined} strokeWidth={props.isFiltered ? 1.5 : undefined} />
            </IconButton>
            <FiltersDialog>
              {props.children}
            </FiltersDialog>
          </DialogTrigger>
          <Tooltip>Filter</Tooltip>
        </TooltipTrigger>}

      {isSearchActive &&
        <View slot={null!} style={mobileOverlayStyles.base}>
          <View slot={null!} style={mobileOverlayStyles.searchBarRow}>
            <SearchBar
              value={props.value}
              onChange={props.onChange}
              onClear={props.onClear}
              onSubmit={() => setIsSearchActive(false)}
              onCancel={() => {
                if (props.value) {
                  props.onBlurSave(props.value as string);
                }
                setIsSearchActive(false);
              }}
            />
          </View>
          <View slot={null!} style={mobileOverlayStyles.historyList}>
            <GridList
              aria-label="Search history"
              items={items}
              selectedKeys={props.value ? new Set([ props.value ]) : new Set()}
              selectionMode="single"
              style={mobileOverlayStyles.gridList}
              onSelectionChange={(keys) => {
                const selected = [ ...keys ][0] as string | undefined;
                if (selected) {
                  props.onSelectHistory(selected);
                  setIsSearchActive(false);
                }
              }}>
              {(item) => (
                <GridListItem
                  key={item.id}
                  textValue={item.label}>
                  <Text size={16} style={gridListItemStyles.text}>{item.label}</Text>
                  <ButtonCore {...stylex.props(gridListItemStyles.button)} onPress={() => props.onRemoveHistory(props.history.indexOf(item.id))}>
                    <CloseStrokeIcon size={20} />
                  </ButtonCore>
                </GridListItem>
              )}
            </GridList>
          </View>
        </View>}
    </>
  );
}

function SearchFieldWithTooltipClose (props: { isFocused: boolean; children: React.ReactElement }): React.ReactElement {
  const tooltipState = useContext(TooltipTriggerStateContext);

  useEffect(() => {
    if (props.isFocused) {
      tooltipState?.close(true);
    }
  }, [ props.isFocused, tooltipState ]);

  return props.children;
}

interface FilterTooltipButtonProps {
  children: DialogProps['children'];
  onOpenChange: (isOpen: boolean) => void;
}

function FilterTooltipButton (props: FilterTooltipButtonProps): React.ReactElement {
  const searchTooltipState = useContext(TooltipTriggerStateContext);

  const { hoverProps } = useHover({
    onHoverStart: () => searchTooltipState?.close(true),
  });

  return (
    <div {...hoverProps}>
      <DialogTrigger onOpenChange={props.onOpenChange}>
        <FilterIconButtonWithTooltip />
        <FiltersDialog>
          {props.children}
        </FiltersDialog>
      </DialogTrigger>
    </div>
  );
}

function FilterIconButtonWithTooltip (): React.ReactElement {
  const dialogState = useContext(OverlayTriggerStateContext);

  return (
    <TooltipTrigger isDisabled={dialogState?.isOpen}>
      <IconButton slot={SEARCH_FIELD_SLOTS.FILTER_ICON_BUTTON} />
      <Tooltip>Filter</Tooltip>
    </TooltipTrigger>
  );
}

interface DesktopSearchProps extends Pick<SearchFieldProps, 'value' | 'onChange' | 'onClear'>, Pick<DialogProps, 'children'>, HistoryProps {}

function DesktopSearch (props: DesktopSearchProps) {
  const [ isFocusWithin, setIsFocusWithin ] = useState(false);
  const [ isFilterDialogOpen, setIsFilterDialogOpen ] = useState(false);
  const filterDialogWasOpenRef = useRef(false);
  const [ isPopoverFocusWithin, setIsPopoverFocusWithin ] = useState(false);
  const isPopoverFocusWithinRef = useRef(false);
  const [ menuWidth, setMenuWidth ] = useState('0px');
  const triggerRef = useRef<HTMLDivElement>(null);

  const onResize = () => {
    if (triggerRef.current) {
      setMenuWidth(triggerRef.current.getBoundingClientRect().width + 'px');
    }
  };

  useResizeObserver({ ref: triggerRef, onResize });

  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange: (focused) => {
      if (focused && filterDialogWasOpenRef.current) {
        filterDialogWasOpenRef.current = false;
        return;
      }
      setIsFocusWithin(focused);
    },
    onBlurWithin: () => {
      if (!isPopoverFocusWithinRef.current && props.value) {
        props.onBlurSave(props.value as string);
      }
    },
  });

  const { focusWithinProps: popoverFocusWithinProps } = useFocusWithin({
    onFocusWithinChange: (focused) => {
      isPopoverFocusWithinRef.current = focused;
      setIsPopoverFocusWithin(focused);
    },
  });

  useHotkey('Mod+K', (event) => {
    event.preventDefault();
    triggerRef.current?.querySelector('input')?.focus();
  }, { requireReset: true });

  const items = props.history
    .filter((item) => item.trim() !== props.value?.trim())
    .slice(0, 5)
    .map((item, index) => ({ id: item, index, label: item }));

  const shouldShowHistory = (isFocusWithin || isPopoverFocusWithin) && items.length > 0;

  return (
    <div ref={triggerRef} {...focusWithinProps}>
      <TooltipTrigger isDisabled={isFilterDialogOpen}>
        <SearchFieldWithTooltipClose isFocused={isFocusWithin}>
          <SearchField
            value={props.value}
            onChange={props.onChange}
            onClear={props.onClear}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                (e.target as HTMLElement).blur();
              }
            }}>
            {(renderProps) =>
              <>
                {renderProps.defaultChildren}
                {props.children &&
                  <FilterTooltipButton onOpenChange={(isOpen) => {
                    if (isOpen) {
                      filterDialogWasOpenRef.current = true;
                    }
                    setIsFilterDialogOpen(isOpen);
                  }}>
                    {props.children}
                  </FilterTooltipButton>}
              </>}
          </SearchField>
        </SearchFieldWithTooltipClose>
        <Tooltip placement="bottom">
          <Text>Search</Text>
          <Kbd>{formatForDisplay('Mod+K')}</Kbd>
        </Tooltip>
      </TooltipTrigger>

      <Popover
        isNonModal
        isOpen={shouldShowHistory}
        offset={3}
        placement="bottom start"
        style={desktopHistoryStyles.popover(menuWidth)}
        triggerRef={triggerRef}>
        <div {...popoverFocusWithinProps}>
          <GridList
            aria-label="Search history"
            items={items}
            selectedKeys={props.value ? new Set([ props.value as string ]) : new Set()}
            selectionMode="single"
            onSelectionChange={(keys) => {
              const selected = [ ...keys ][0] as string | undefined;
              if (selected) {
                props.onSelectHistory(selected);
              }
            }}>
            {(item) => (
              <GridListItem
                key={item.id}
                textValue={item.label}>
                <Text style={gridListItemStyles.text}>{item.label}</Text>
                <ButtonCore {...stylex.props(gridListItemStyles.button)} onPress={() => props.onRemoveHistory(item.index)}>
                  <CloseStrokeIcon size={20} />
                </ButtonCore>
              </GridListItem>
            )}
          </GridList>
        </div>
      </Popover>
    </div>
  );
}

function FiltersDialog (props: DialogProps) {
  return (
    <Dialog {...props} style={[ dialogStyles.base, props.style ]}>
      {(dialogRenderProps) => (
        <>
          <DialogBar slot={DIALOG_SLOTS.TOP_BAR}>
            <Heading>Filters</Heading>
            <TooltipTrigger>
              <IconButton aria-label="Close" slot={DIALOG_SLOTS.CLOSE} />
              <Tooltip>Close</Tooltip>
            </TooltipTrigger>
          </DialogBar>

          <Provider values={[
            [ ViewContext, { slot: null }],
            [
              ButtonContext, {
                slots: {
                  [DEFAULT_SLOT]: {},
                  [FORM_SLOTS.RETRY_TEXT_BUTTON]: {},
                },
              },
            ],
          ]}>
            {typeof props.children === 'function' ? props.children(dialogRenderProps) : props.children}
          </Provider>
        </>
      )}
    </Dialog>
  );
}

const mobileOverlayStyles = stylex.create({
  base: {
    backgroundColor: $.surfaceBGPrimary,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    left: 0,
    position: 'fixed',
    right: 0,
    top: 0,
    zIndex: 2,
  },
  searchBarRow: {
    paddingBottom: 12,
    paddingInline: 20,
    paddingTop: 56,
  },
  historyList: {
    flex: '1 1 0',
    overflow: 'hidden',
    paddingBottom: 8,
    paddingInline: 4,
    paddingTop: 16,
  },
  gridList: {
    maxHeight: '100%',
    scrollbarWidth: 'none',
  },
});

const desktopHistoryStyles = stylex.create({
  popover: (width: string) => ({
    backgroundColor: $.surfaceBGAlternative01,
    borderColor: $.border08,
    borderRadius: 8,
    borderStyle: 'solid',
    borderWidth: 1,
    boxShadow: $.shadowBottom060Medium,
    maxHeight: 320,
    overflowY: 'auto',
    padding: 4,
    width,
  }),
});

const dialogStyles = stylex.create({
  base: {
    margin: {
      default: 0,
      [breakpoints.small]: 8,
    },
    width: {
      default: 520,
      [breakpoints.small]: '100%',
    },
  },
});

const gridListItemStyles = stylex.create({
  text: {
    gridColumn: 'span 2',
  },
  button: {
    backgroundColor: 'transparent',
    borderStyle: 'none',
    cursor: 'pointer',
    padding: 0,
  },
});
